import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Users, Palette, BarChart3, Calendar, Bell, Settings, Search, Menu, X,
  Home, LogOut, Shield, Building2, Activity, BookOpen, TrendingUp, UserCheck,
  MessageSquare, ChevronDown, Filter, Download, RefreshCw, Zap, Image, FileText, Volume2,
  ShoppingBag
} from 'lucide-react';
import { supabase } from '../utils/supabaseClient';
import '../styles/AdminStyles.css';

const AdminLayout = ({ children, adminUser: propAdminUser }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');
  const [notifications, setNotifications] = useState(3);
  const [adminUser, setAdminUser] = useState(propAdminUser);
  const navigate = useNavigate();
  const location = useLocation();

  // Update adminUser when prop changes
  useEffect(() => {
    setAdminUser(propAdminUser);
  }, [propAdminUser]);

  // Update active section based on current path
  useEffect(() => {
    const path = location.pathname;
    if (path === '/admin/users') setActiveSection('users');
    else if (path === '/admin/artists') setActiveSection('artists');
    else if (path === '/admin/galleries') setActiveSection('galleries');
    else if (path === '/admin/collections') setActiveSection('collections');
    else if (path === '/admin/submissions') setActiveSection('submissions');
    else if (path === '/admin/events') setActiveSection('events');
    else if (path === '/admin/analytics') setActiveSection('analytics');
    else if (path === '/admin/notifications') setActiveSection('notifications');
    else if (path === '/admin/pulse') setActiveSection('pulse');
    else if (path === '/admin/settings') setActiveSection('settings');
    else if (path === '/admin/dashboard' || path === '/admin' || path === '/') setActiveSection('overview');
    else if (path === '/admin/artworks') setActiveSection('artworks');
    else if (path === '/admin/orders') setActiveSection('orders');
  }, [location.pathname]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuOpen && !event.target.closest('.mobile-menu') && !event.target.closest('.admin-menu-toggle')) {
        setMobileMenuOpen(false);
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [mobileMenuOpen]);

  const navigationItems = [
    { id: 'overview', label: 'Overview', icon: Home, path: '/admin/dashboard' },
    { id: 'users', label: 'User Management', icon: Users, path: '/admin/users' },
    { id: 'artists', label: 'Artist Dashboard', icon: Palette, path: '/admin/artists' },
    { id: 'orders', label: 'Orders', icon: ShoppingBag, path: '/admin/orders' },
    { id: 'artworks', label: 'Artwork Management', icon: Image, path: '/admin/artworks' },
    { id: 'audio-artworks', label: 'Audio Artworks', icon: Volume2, path: '/admin/audio-artworks' },
    { id: 'galleries', label: 'Gallery Management', icon: Building2, path: '/admin/galleries' },
    { id: 'collections', label: 'Collection Management', icon: BookOpen, path: '/admin/collections' },
    { id: 'submissions', label: 'Submissions', icon: FileText, path: '/admin/submissions' },
    { id: 'events', label: 'Events & Exhibitions', icon: Calendar, path: '/admin/events' },
    { id: 'analytics', label: 'Analytics', icon: BarChart3, path: '/admin/analytics' },
    { id: 'notifications', label: 'Notifications', icon: Bell, badge: notifications, path: '/admin/notifications' },
    { id: 'pulse', label: 'SmartPulse', icon: Zap, path: '/admin/pulse' },
    { id: 'settings', label: 'Settings', icon: Settings, path: '/admin/settings' }
  ];

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileNavigation = (sectionId, path) => {
    setActiveSection(sectionId);
    setMobileMenuOpen(false); // Close mobile menu after navigation
    if (path) navigate(path);
  };

  return (
    <div className="admin-dashboard">
      {/* Sidebar */}
      <div className={`admin-sidebar ${sidebarOpen ? '' : 'collapsed'}`}>
        {/* Logo & Brand */}
        <div className="admin-sidebar-header">
          <div className="admin-sidebar-brand">
            <div className="admin-sidebar-logo">
              <img 
                src="/images/logo-120.png" 
                alt="VibrantCast"
              />
            </div>
            {sidebarOpen && (
              <div className="admin-sidebar-brand-text">
                <h1>VibrantCast</h1>
                <p>Admin Portal</p>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="admin-sidebar-nav">
          <ul className="admin-nav-list">
            {navigationItems.map((item) => (
              <li key={item.id} className="admin-nav-item">
                <button
                  onClick={() => {
                    setActiveSection(item.id);
                    if (item.path) navigate(item.path);
                  }}
                  className={`admin-nav-button ${
                    activeSection === item.id ? 'active' : ''
                  }`}
                >
                  <item.icon className="admin-nav-icon" />
                  {sidebarOpen && (
                    <>
                      <span>{item.label}</span>
                      {item.badge && (
                        <span className="admin-nav-badge">
                          {item.badge}
                        </span>
                      )}
                    </>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* User Profile */}
        <div className="admin-sidebar-user">
          <div className="admin-user-info">
            <div className="admin-user-avatar">
              {adminUser?.profile_img ? (
                <img src={adminUser.profile_img} alt="Admin" />
              ) : (
                <span>
                  {adminUser?.first_name?.charAt(0) || 'A'}
                </span>
              )}
            </div>
            {sidebarOpen && (
              <div className="admin-user-details">
                <p className="admin-user-name">
                  {adminUser?.first_name} {adminUser?.last_name}
                </p>
                <p className="admin-user-role">{adminUser?.role}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={`admin-main ${!sidebarOpen ? 'sidebar-collapsed' : ''}`}>
        {/* Top Header */}
        <header className="admin-header">
          <div className="admin-header-content">
            <div className="admin-header-left">
              <button
                onClick={handleMobileMenuToggle}
                className="admin-menu-toggle mobile-menu-toggle"
              >
                <Menu />
              </button>
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="admin-menu-toggle desktop-menu-toggle"
              >
                <Menu />
              </button>
              <div className="admin-header-title">
                <h1>
                  {navigationItems.find(item => item.id === activeSection)?.label || 'Dashboard'}
                </h1>
                <p>Manage your VibrantCast platform</p>
              </div>
            </div>

            <div className="admin-header-right">
              {/* Search */}
              <div className="admin-search-wrapper">
                <Search className="admin-search-icon" />
                <input
                  type="text"
                  placeholder="Search..."
                  className="admin-search-input"
                />
              </div>

              {/* Quick Actions */}
              <button className="admin-header-btn">
                <Bell />
                {notifications > 0 && (
                  <span className="admin-notification-badge">
                    {notifications}
                  </span>
                )}
              </button>

              <button className="admin-header-btn">
                <Settings />
              </button>

              <button 
                onClick={handleLogout}
                className="admin-header-btn"
              >
                <LogOut />
              </button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="admin-content">
          {children}
        </main>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="mobile-menu-overlay">
          <div className="mobile-menu">
            {/* Mobile Menu Header */}
            <div className="mobile-menu-header">
              <div className="mobile-menu-brand">
                <img 
                  src="/images/logo-120.png" 
                  alt="VibrantCast"
                  className="mobile-menu-logo"
                />
                <div className="mobile-menu-brand-text">
                  <h2>VibrantCast</h2>
                  <p>Admin Portal</p>
                </div>
              </div>
              <button
                onClick={handleMobileMenuToggle}
                className="mobile-menu-close"
              >
                <X size={24} color="#ffffff" />
              </button>
            </div>

            {/* Mobile Navigation */}
            <nav className="mobile-menu-nav">
              <ul className="mobile-nav-list">
                {navigationItems.map((item) => (
                  <li key={item.id} className="mobile-nav-item">
                    <button
                      onClick={() => handleMobileNavigation(item.id, item.path)}
                      className={`mobile-nav-button ${
                        activeSection === item.id ? 'active' : ''
                      }`}
                    >
                      <item.icon className="mobile-nav-icon" />
                      <span>{item.label}</span>
                      {item.badge && (
                        <span className="mobile-nav-badge">
                          {item.badge}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Mobile User Profile */}
            <div className="mobile-menu-user">
              <div className="mobile-user-info">
                <div className="mobile-user-avatar">
                  {adminUser?.profile_img ? (
                    <img src={adminUser.profile_img} alt="Admin" />
                  ) : (
                    <span>
                      {adminUser?.first_name?.charAt(0) || 'A'}
                    </span>
                  )}
                </div>
                <div className="mobile-user-details">
                  <p className="mobile-user-name">
                    {adminUser?.first_name} {adminUser?.last_name}
                  </p>
                  <p className="mobile-user-role">{adminUser?.role}</p>
                </div>
              </div>
              <button 
                onClick={handleLogout}
                className="mobile-logout-button"
              >
                <LogOut size={20} color="#ffffff" />
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLayout;
